import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as admin from '../admin'
import type * as agent from '../agent'
import type * as common from '../common'
import type * as dms from '../dms'
import type * as integrations from '../integrations'
import type * as mosaic from '../mosaic'

// TODO: many of these commands are repetitive with our controllers (e.g.
// payload === parameters). It would be cool if we could have a system where
// controllers are automatically available as commands without having to copy
// over the information here. This would probably fit well as part of an overall
// initiative to split up our handlers, controllers, and maybe even entities
// into smaller, more independent systems (e.g. quiver as its own controllers &
// entities, rather than embedded into our full site controllers and entities)

/**
 * Represents a command to be run in our system
 *
 * __NOTE:__ this is a discriminated union on {@link Command.operation}, which
 * means the type of {@link Command.payload} can be determined by checking
 * {@link Command.operation}
 *
 * @example
 * if (command.operation === Command.Operation.DmsMutate) {
 *   // command.payload is of type Payload<Operation.DmsMutate>
 * }
 */
export type Command<TOp extends Command.Operation = Command.Operation> =
  TOp extends any
    ? AllowMissing<{
        /** @see {@link Command.Operation} */
        operation: TOp
        /** @see {@link Command.Payload} */
        payload: Command.Payload<TOp>
      }>
    : never
export namespace Command {
  /**
   * An operation that can be executed in the Attain Apps system through a
   * {@link Command}
   *
   * {@link Command.Payload} contains the information needed to execute this
   * {@link Operation}
   */
  export enum Operation {
    /**
     * TODO:
     */
    AdminAppsCreate = 'admin.apps.create',
    /**
     * TODO:
     */
    AdminAppsUpdate = 'admin.apps.update',
    /**
     * TODO:
     */
    AdminAppsDelete = 'admin.apps.delete',
    /**
     * TODO:
     */
    AdminAppsPagesAdd = 'admin.apps.pages.add',
    /**
     * TODO:
     */
    AdminAppsPagesUpdate = 'admin.apps.pages.update',
    /**
     * TODO:
     */
    AdminAppsPagesRemove = 'admin.apps.pages.remove',
    /**
     * Assigns an agent to the current user in the zone
     */
    AdminAgentsAssign = 'admin.agents.assign',
    IntegrationsConnectorDefinitionsCreate = 'integrations.connectorDefinitions.create',
    IntegrationsConnectorDefinitionsUpdate = 'integrations.connectorDefinitions.update',
    /**
     * TODO:
     */
    DmsAttributesCreate = 'dms.attributes.create',
    /**
     * TODO:
     */
    DmsAttributesUpdate = 'dms.attributes.update',
    /**
     * TODO:
     */
    DmsAttributesDelete = 'dms.attributes.delete',
    /**
     * TODO:
     */
    DmsDimensionsCreate = 'dms.dimensions.create',
    /**
     * TODO:
     */
    DmsDimensionsUpdate = 'dms.dimensions.update',
    /**
     * TODO:
     */
    DmsDimensionsDelete = 'dms.dimensions.delete',
    /**
     * TODO:
     */
    DmsEntitiesCreate = 'dms.entities.create',
    /**
     * TODO:
     */
    DmsEntitiesUpdate = 'dms.entities.update',
    /**
     * TODO:
     */
    DmsEntitiesDelete = 'dms.entities.delete',
    /**
     * TODO:
     */
    DmsEnumsCreate = 'dms.enums.create',
    /**
     * TODO:
     */
    DmsEnumsUpdate = 'dms.enums.update',
    /**
     * TODO:
     */
    DmsEnumsDelete = 'dms.enums.delete',
    /**
     * TODO:
     */
    DmsEnumsEntriesCreate = 'dms.enums.entries.create',
    /**
     * TODO:
     */
    DmsEnumsEntriesUpdate = 'dms.enums.entries.update',
    /**
     * TODO:
     */
    DmsEnumsEntriesDelete = 'dms.enums.entries.delete',
    /**
     * TODO:
     */
    DmsPurposesCreate = 'dms.purposes.create',
    /**
     * TODO:
     */
    DmsPurposesUpdate = 'dms.purposes.update',
    /**
     * TODO:
     */
    DmsPurposesDelete = 'dms.purposes.delete',
    /**
     * TODO:
     */
    DmsSnapshotsCreate = 'dms.snapshots.create',
    /**
     * TODO:
     */
    DmsSnapshotsUpdate = 'dms.snapshots.update',
    /**
     * TODO:
     */
    DmsSnapshotsDelete = 'dms.snapshots.delete',
    /**
     * TODO:
     */
    DmsStructuresCreate = 'dms.structures.create',
    /**
     * TODO:
     */
    DmsStructuresUpdate = 'dms.structures.update',
    /**
     * TODO:
     */
    DmsStructuresDelete = 'dms.structures.delete',
    /**
     * Applies changes in the DMS schema to the underlying database. Recommended
     * anytime any changes are made to the schema
     */
    DmsSchemaApplyChanges = 'dms.schema.applyChanges',
    /**
     * Executes a {@link dms.data.Mutation}
     */
    DmsDataMutate = 'dms.data.mutate',
    /**
     * TODO:
     */
    // DmsGenerateTestData = 'dms.data.generate',
    /**
     * TODO:
     */
    MosaicWidgetsCreate = 'mosaic.widgets.create',
    /**
     * TODO:
     */
    MosaicWidgetsUpdate = 'mosaic.widgets.update',
    /**
     * TODO:
     */
    MosaicWidgetsDelete = 'mosaic.widgets.delete',
    /**
     * TODO:
     */
    MosaicActionsCreate = 'mosaic.actions.create',
    /**
     * TODO:
     */
    MosaicActionsUpdate = 'mosaic.actions.update',
    /**
     * TODO:
     */
    MosaicActionsDelete = 'mosaic.actions.delete',
    /**
     * TODO:
     */
    MosaicPagesCreate = 'mosaic.pages.create',
    /**
     * TODO:
     */
    MosaicPagesUpdate = 'mosaic.pages.update',
    /**
     * TODO:
     */
    MosaicPagesDelete = 'mosaic.pages.delete',
    /**
     * TODO: starts an approval request
     */
    ApprovalsRequest = 'approvals.request',
    /**
     * TODO: approves, rejects, or requests modification from an approval
     */
    ApprovalsRespond = 'approvals.respond',
    // TODO: how do we want to add comments to approvals, either from the
    // requester or reviewer, or maybe even someone else?
  }
  // Needs to be before namespace is defined
  const OPERATIONS: ReadonlySet<Operation> = new Set(
    Object.values(Operation) as Operation[],
  )
  export namespace Operation {
    export const ALL: readonly Operation[] = Array.from(OPERATIONS)
    export const isOperation = TypeChecker.Factory.oneOf(OPERATIONS)
    export const assertIsOperation: TypeAsserter<Operation> =
      TypeAsserter.Factory.from(isOperation)
  }

  /**
   * Contains necessary information to execute a given {@link Operation}
   */
  export type Payload<TOp extends Operation> = {
    [Operation.AdminAppsCreate]: {
      readonly input: admin.App.Input
    }
    [Operation.AdminAppsDelete]: { readonly id: admin.App.Id }
    [Operation.AdminAppsUpdate]: {
      readonly id: admin.App.Id
      readonly updates: admin.App.Updates
    }
    [Operation.AdminAppsPagesAdd]: {
      readonly app: common.FlexibleIdentifier<admin.App.Id>
      readonly pages: readonly admin.App.Page.Input[]
    }
    [Operation.AdminAppsPagesRemove]: {
      readonly app: common.FlexibleIdentifier<admin.App.Id>
      readonly ids: readonly admin.App.Page.Id[]
    }
    [Operation.AdminAppsPagesUpdate]: {
      readonly app: common.FlexibleIdentifier<admin.App.Id>
      readonly id: admin.App.Page.Id
      readonly updates: admin.App.Page.Updates
    }
    [Operation.AdminAgentsAssign]: {
      readonly agentId: agent.Agent.Id
      readonly pageContext?: agent.ZoneConfig.PageContext
    }
    [Operation.IntegrationsConnectorDefinitionsCreate]: {
      readonly definition: integrations.Connector.Definition.Input
    }
    [Operation.IntegrationsConnectorDefinitionsUpdate]: {
      readonly definitionId: integrations.Connector.Definition.Id
      readonly definition: integrations.Connector.Definition.Updates
    }
    [Operation.DmsAttributesCreate]: {
      readonly input: dms.models.Attribute.Input
    }
    [Operation.DmsAttributesDelete]: { readonly id: dms.models.Attribute.Id }
    [Operation.DmsAttributesUpdate]: {
      readonly id: dms.models.Attribute.Id
      readonly updates: dms.models.Attribute.Input
    }
    [Operation.DmsDimensionsCreate]: {
      readonly input: dms.models.Dimension.Input
    }
    [Operation.DmsDimensionsDelete]: { readonly id: dms.models.Dimension.Id }
    [Operation.DmsDimensionsUpdate]: {
      readonly id: dms.models.Dimension.Id
      readonly updates: dms.models.Dimension.Updates
    }
    [Operation.DmsEntitiesCreate]: {
      readonly input: dms.models.Entity.Input
    }
    [Operation.DmsEntitiesDelete]: { readonly id: dms.models.Entity.Id }
    [Operation.DmsEntitiesUpdate]: {
      readonly id: dms.models.Entity.Id
      readonly updates: dms.models.Entity.Updates
    }
    [Operation.DmsEnumsCreate]: { readonly input: dms.models.Enum.Input }
    [Operation.DmsEnumsDelete]: { readonly id: dms.models.Enum.Id }
    [Operation.DmsEnumsUpdate]: {
      readonly id: dms.models.Enum.Id
      readonly updates: dms.models.Enum.Updates
    }
    [Operation.DmsEnumsEntriesCreate]: {
      readonly enumId: dms.models.Enum.Id
      readonly input: dms.models.Enum.Entry.Input
    }
    [Operation.DmsEnumsEntriesDelete]: {
      readonly enumId: dms.models.Enum.Id
      readonly id: dms.models.Enum.Entry.Id
    }
    [Operation.DmsEnumsEntriesUpdate]: {
      readonly enumId: dms.models.Enum.Id
      readonly id: dms.models.Enum.Entry.Id
      readonly updates: dms.models.Enum.Entry.Input
    }
    [Operation.DmsPurposesCreate]: { readonly input: dms.models.Purpose.Input }
    [Operation.DmsPurposesDelete]: { readonly id: dms.models.Purpose.Id }
    [Operation.DmsPurposesUpdate]: {
      readonly id: dms.models.Purpose.Id
      readonly updates: dms.models.Purpose.Updates
    }
    [Operation.DmsSchemaApplyChanges]: undefined
    [Operation.DmsSnapshotsCreate]: {
      readonly input: dms.models.Snapshot.Input
    }
    [Operation.DmsSnapshotsDelete]: { readonly id: dms.models.Snapshot.Id }
    [Operation.DmsSnapshotsUpdate]: {
      readonly id: dms.models.Snapshot.Id
      readonly updates: dms.models.Snapshot.Updates
    }
    [Operation.DmsStructuresCreate]: {
      readonly input: dms.models.Structure.Input
    }
    [Operation.DmsStructuresDelete]: { readonly id: dms.models.Structure.Id }
    [Operation.DmsStructuresUpdate]: {
      readonly id: dms.models.Structure.Id
      readonly updates: dms.models.Structure.Updates
    }
    [Operation.DmsDataMutate]: Maybe.ReadonlyArray<dms.data.Mutation>
    [Operation.MosaicWidgetsCreate]: {
      readonly input: mosaic.Widget.Input
    }
    [Operation.MosaicWidgetsDelete]: {
      readonly identifier: common.FlexibleIdentifier<mosaic.Widget.Id>
    }
    [Operation.MosaicWidgetsUpdate]: {
      readonly identifier: common.FlexibleIdentifier<mosaic.Widget.Id>
      readonly updates: mosaic.Widget.Updates
    }
    [Operation.MosaicActionsCreate]: {
      readonly input: mosaic.Action.Input
    }
    [Operation.MosaicActionsDelete]: {
      readonly identifier: common.FlexibleIdentifier<mosaic.Action.Id>
    }
    [Operation.MosaicActionsUpdate]: {
      readonly identifier: common.FlexibleIdentifier<mosaic.Action.Id>
      readonly updates: mosaic.Action.Updates
    }
    [Operation.MosaicPagesCreate]: {
      readonly input: mosaic.Page.Input
    }
    [Operation.MosaicPagesDelete]: {
      readonly identifier: common.FlexibleIdentifier<mosaic.Page.Id>
    }
    [Operation.MosaicPagesUpdate]: {
      readonly identifier: common.FlexibleIdentifier<mosaic.Page.Id>
      readonly updates: mosaic.Page.Updates
    }
    [Operation.ApprovalsRequest]: unknown
    [Operation.ApprovalsRespond]: unknown
  }[TOp]
}
